import { ReactNode } from "react";
import { Info } from "components/icons/Info";
import classNames from "classnames";
import { Button } from "../Button/Button";
import { Cross } from "components/icons/Cross";
import { Warning } from "components/icons/Warning";
import { TriangleWarning } from "components/icons/TriangleWarning";

type BannerPropsType = {
  type: "success" | "error" | "warning" | "info" | "primary" | "neutral";
  title?: ReactNode;
  description: ReactNode;
  descriptionClassName?: string;
  onClose?: () => void;
  cta?: ReactNode;
  children?: ReactNode;
  icon?: ReactNode | null;
  className?: string;
  titleClassName?: string;
};

const DefaultIcon = ({ type }: { type: BannerPropsType["type"] }) => {
  if (type === "warning") {
    return <TriangleWarning />;
  }
  return <Info color="currentColor" stroke="1.2" size="16" />;
};

export const Banner = ({
  type,
  cta,
  title,
  onClose,
  description,
  descriptionClassName,
  children,
  icon,
  className,
  titleClassName,
}: BannerPropsType) => {
  return (
    <div
      className={classNames(
        "t-flex t-p-2.5 t-border t-border-solid t-rounded-lg t-gap-2 t-items-center sm:t-flex-wrap sm:t-items-start",
        {
          "t-bg-red-0 t-border-red-10": type === "error",
          "t-border-green-20 t-bg-green-10": type === "success",
          "t-border-orange-10 t-bg-orange-0": type === "warning",
          "t-bg-blue-0 t-border-blue-20": type === "info",
          "t-border-purple-10 t-bg-purple-0": type === "primary",
          "t-border-neutral-0 t-bg-surface-lighter-grey": type === "neutral",
        },
        className
      )}
    >
      {icon ? (
        icon
      ) : (
        <span
          className={classNames("t-flex t-shrink-0 t-self-start", {
            "t-text-red": type === "error",
            "t-text-green": type === "success",
            "t-text-orange": type === "warning",
            "t-text-blue-80": type === "info",
            "t-text-purple": type === "primary",
            "t-text-text-30": type === "neutral",
          })}
        >
          <DefaultIcon type={type} />
        </span>
      )}
      <div className="t-space-y-1 sm:t-flex-1 sm:t-min-w-0">
        {title && (
          <p
            className={classNames(
              "t-m-0 t-text-subtext-sm",
              titleClassName || "t-text-text-60"
            )}
          >
            {title}
          </p>
        )}
        <p
          className={classNames("t-text-body-sm t-mb-0", {
            "t-text-text-30": !descriptionClassName,
            [descriptionClassName || ""]: Boolean(descriptionClassName),
          })}
        >
          {description}
        </p>
      </div>
      {cta && (
        <div className="t-ml-auto t-mr-2 sm:t-order-last sm:t-basis-full sm:t-ml-6">
          {cta}
        </div>
      )}
      {onClose && (
        <div
          className={classNames({
            "t-ml-auto": !cta,
          })}
        >
          <Button customType="ghost_icon" size="xsmall" onClick={onClose}>
            <Cross color="currentColor" />
          </Button>
        </div>
      )}
    </div>
  );
};
